import {
  forwardRef,
  HTMLAttributes,
  PropsWithChildren,
  ReactElement,
  Ref,
  SyntheticEvent,
  useState,
} from "react";
import {
  Autocomplete,
  AutocompleteProps,
  createFilterOptions,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import isEmpty from "lodash/isEmpty";
import TextField, { TTextFieldProps } from "components/shared/TextField";
import Loading from "../Loading";
import styles from "./SingleSelectTypeahead.styles";

type TSingleSelectTypeaheadOptionProps = {
  noWrap?: boolean;
};

export type TSingleSelectTypeaheadOption = {
  id?: string;
  label: string;
  value: string;
  disabled?: boolean;
};

export type TSingleSelectTypeaheadProps<
  OptionType extends
    TSingleSelectTypeaheadOption = TSingleSelectTypeaheadOption,
> = Omit<
  AutocompleteProps<
    OptionType, // type of value
    false, // Multiple
    boolean, // DisableClearable
    false // FreeSolo
  >,
  "renderInput"
> & {
  testId?: string;
  required?: boolean;
  error?: boolean;
  label?: string;
  helperText?: string;
  emptyLabel?: string;
  OptionProps?: TSingleSelectTypeaheadOptionProps;
  textFieldProps?: TTextFieldProps;
  placeholder?: string;
  allowOptionCreation?: boolean;
  optionCreationHelpText?: string;
  getInputStartAdornment?: (
    value: OptionType | null | undefined,
  ) => ReactElement | string | null;
  onClose?: () => void;
};

export function Option<
  OptionType extends
    TSingleSelectTypeaheadOption = TSingleSelectTypeaheadOption,
>({
  children,
  optionProps,
  option,
  noWrap,
}: PropsWithChildren<{
  optionProps: HTMLAttributes<HTMLLIElement>;
  option: OptionType;
  noWrap?: boolean;
}>) {
  const optionStyles = {
    ...styles.option,
    ...(noWrap && styles.noWrapOption),
  };

  return (
    <MenuItem
      {...optionProps}
      disabled={option.disabled}
      sx={optionStyles}
      key={`single-select-menu-option-${option.value}`}
    >
      <Stack direction="row" key={`single-select-text-${option.value}`}>
        {children || (
          <Typography
            noWrap={noWrap}
            key={`single-select-text-${option.value}`}
          >
            {option.label}
          </Typography>
        )}
      </Stack>
    </MenuItem>
  );
}

const SingleSelectTypeahead = <
  OptionType extends
    TSingleSelectTypeaheadOption = TSingleSelectTypeaheadOption,
>(
  props: TSingleSelectTypeaheadProps<OptionType>,
  ref: Ref<HTMLDivElement>,
) => {
  const {
    testId,
    error,
    label,
    required,
    helperText,
    placeholder,
    emptyLabel,
    textFieldProps,
    OptionProps,
    onClose,
    getInputStartAdornment,
    allowOptionCreation = false,
    loading,
    loadingText,
    onOpen,
    ...autocompleteProps
  } = props;

  const [isOpen, setIsOpen] = useState(false);

  const filter = createFilterOptions<OptionType>();
  const isDropdownOpen = autocompleteProps.open ?? isOpen;
  const shouldShowLoading = loading && isDropdownOpen;

  return (
    <Autocomplete<OptionType, false, boolean, false>
      data-testid={testId}
      data-qaid={testId}
      renderOption={(optionProps, option) => (
        <Option<OptionType>
          key={option.value}
          optionProps={optionProps}
          option={option}
          {...OptionProps}
        />
      )}
      {...(allowOptionCreation && {
        filterOptions: (options, params) => {
          const filtered = filter(options, params);
          const { inputValue } = params;

          if (
            !isEmpty(inputValue) &&
            !options.some(({ label }) => label === inputValue)
          ) {
            return [
              { label: inputValue, value: "" },
              ...filtered,
            ] as OptionType[];
          }

          return filtered as OptionType[];
        },
      })}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      renderInput={({ InputProps: { ...restInputProps }, ...restParams }) => {
        const inputProps = {
          ...restInputProps,
          "data-qaid": `${testId}-input`,
        };

        return (
          <TextField
            ref={ref}
            label={label}
            emptyLabel={emptyLabel}
            error={error}
            helperText={helperText}
            placeholder={placeholder}
            required={required}
            slotProps={{
              input: {
                ...inputProps,
                startAdornment: autocompleteProps.value
                  ? getInputStartAdornment?.(autocompleteProps.value)
                  : null,
                endAdornment: (
                  <>
                    {shouldShowLoading ? <Loading size={20} /> : null}
                    {inputProps.endAdornment}
                  </>
                ),
              },
              formHelperText: {
                variant: "standard",
                "aria-live": "polite",
              },
            }}
            {...restParams}
            {...textFieldProps}
          />
        );
      }}
      sx={styles.autocomplete}
      onOpen={(event: SyntheticEvent) => {
        setIsOpen(true);
        onOpen?.(event);
      }}
      onClose={() => {
        setIsOpen(false);
        onClose?.();
      }}
      loading={shouldShowLoading}
      loadingText={loadingText}
      {...autocompleteProps}
    />
  );
};

export default forwardRef(SingleSelectTypeahead) as <
  OptionType extends TSingleSelectTypeaheadOption,
>(
  props: TSingleSelectTypeaheadProps<OptionType>,
  ref?: Ref<HTMLDivElement>,
) => ReturnType<typeof SingleSelectTypeahead>;
